import * as React from 'react'
import debounce from 'lodash/debounce'
import {useLocation} from 'react-router'
import useSWR from 'swr'
import {fromMaybe} from '@freckle/maybe'
import {PATHS} from '@frontrow/console/src/entities/helpers/paths'

import {
  sendImpersonateSchoolAdmin,
  fetchSchoolAdmins,
  patchSchoolAdmin,
  addSchoolAdminDistrict,
  deleteSchoolAdminDistrict,
  addSchoolAdminSchools,
  deleteSchoolAdminSchools,
  deleteSchoolAdmin
} from '@frontrow/console/src/helpers/api-school-admins'
import SchoolAdminsDataTable from '@frontrow/console/src/components/pages/school-admins-page/school-admins-data-table'
import {type FetchDataParamsT} from '@frontrow/console/src/components/common/data-table'
import {
  type DetailedSchoolAdminT,
  type SchoolAdminUpdateT
} from '@frontrow/console/src/models/school-admin'
import {parseFilterUrl} from '@frontrow/console/src/helpers/data-table'
import {firstResponseApiError} from '@frontrow/console/src/entities/models/api-error'

const SCHOOL_ADMINS_SWR_KEY = 'school-admins'

function impersonateSchoolAdmin(schoolAdminEmail: string) {
  const schoolDashWindow = window.open('', '_blank')
  schoolDashWindow!.document.body.innerHTML = 'Authenticating . . .'

  sendImpersonateSchoolAdmin(schoolAdminEmail).then(
    () => {
      schoolDashWindow!.document.body.innerHTML = 'Loading . . .'
      schoolDashWindow!.location.href = PATHS.schoolDashUrl
    },
    () => {
      alert("School admin doesn't exist")
      schoolDashWindow!.close()
    }
  )
}

function parseTextErrors(rawErrors: XMLHttpRequest): string {
  const parsedError = JSON.parse(rawErrors.responseText)
  return parsedError.errors[0]
}

export function SchoolAdminsPageContainer(): React.ReactElement {
  const location = useLocation()
  const defaultFiltered = parseFilterUrl(location)
  const [fetchDataParams, setFetchDataParams] = React.useState<FetchDataParamsT | null>(null)
  const [newSchoolAdmin, setNewSchoolAdmin] = React.useState<DetailedSchoolAdminT | null>(null)
  const [errors, setErrors] = React.useState<Array<string>>([])

  const {data, error, isLoading, mutate} = useSWR(
    [SCHOOL_ADMINS_SWR_KEY, fetchDataParams],
    async ([_, params]) => {
      const response = await fetchSchoolAdmins(params)
      return response
    }
  )

  const fetchData = debounce((params?: FetchDataParamsT | null) => {
    setFetchDataParams(params ?? null)
    setNewSchoolAdmin(null)
  }, 200)

  const handleUpdateSchoolAdmin = async (
    schoolAdminId: number,
    updatedSchoolAdmin: SchoolAdminUpdateT
  ): Promise<void> => {
    try {
      const response = await patchSchoolAdmin(schoolAdminId, updatedSchoolAdmin)

      // Optimistically update the cache
      if (data) {
        const updatedData = data.map(admin => (admin.id === schoolAdminId ? response : admin))
        await mutate(updatedData, {revalidate: true})
      }
    } catch (err: unknown) {
      const apiError = firstResponseApiError(err)
      setErrors(prev => [...prev, apiError.message])
    }
  }

  const handleUpdateSchoolAdminDistricts = async (
    schoolAdminId: number,
    addDistrictId: number,
    deleteDistrictId: number
  ): Promise<void> => {
    const addDistrictPromise = !isNaN(addDistrictId)
      ? addSchoolAdminDistrict(addDistrictId, schoolAdminId).catch(error => {
          const parsedError = parseTextErrors(error)
          setErrors(prev => [...prev, parsedError])
          return parsedError
        })
      : Promise.resolve(undefined)

    const deleteDistrictPromise = !isNaN(deleteDistrictId)
      ? deleteSchoolAdminDistrict(deleteDistrictId, schoolAdminId).catch(error => {
          const parsedError = parseTextErrors(error)
          setErrors(prev => [...prev, parsedError])
          return parsedError
        })
      : Promise.resolve(undefined)

    await Promise.all([addDistrictPromise, deleteDistrictPromise])
    await mutate()
  }

  const handleUpdateSchoolAdminSchools = async (
    schoolAdminId: number,
    addSchoolIds: Array<number>,
    deleteSchoolIds: Array<number>
  ): Promise<void> => {
    const addSchoolsPromise = addSchoolIds.length
      ? addSchoolAdminSchools(addSchoolIds, schoolAdminId).catch(error => {
          const parsedError = parseTextErrors(error)
          setErrors(prev => [...prev, parsedError])
          return parsedError
        })
      : Promise.resolve(undefined)

    const deleteSchoolsPromise = deleteSchoolIds.length
      ? deleteSchoolAdminSchools(deleteSchoolIds, schoolAdminId).catch(error => {
          const parsedError = parseTextErrors(error)
          setErrors(prev => [...prev, parsedError])
          return parsedError
        })
      : Promise.resolve(undefined)

    await Promise.all([addSchoolsPromise, deleteSchoolsPromise])
    await mutate()
  }

  // Remove school admin
  const handleRemoveSchoolAdmin = async (schoolAdminId: number): Promise<void> => {
    try {
      await deleteSchoolAdmin(schoolAdminId)
      await mutate()
    } catch (err: unknown) {
      setErrors(prev => [...prev, String(err)])
    }
  }

  // Set new admin (for UI feedback after creation)
  const schoolAdminNewAdminHandler = (newAdmin: DetailedSchoolAdminT) => {
    setNewSchoolAdmin(newAdmin)
  }

  const schoolAdminsData = fromMaybe(() => [], data ?? null)

  // Add error from SWR if present
  React.useEffect(() => {
    if (error) {
      const apiError = firstResponseApiError(error)
      setErrors(prev => [...prev, apiError.message])
    }
  }, [error])

  return (
    <SchoolAdminsDataTable
      fetchData={fetchData}
      updateSchoolAdmin={handleUpdateSchoolAdmin}
      updateSchoolAdminDistricts={handleUpdateSchoolAdminDistricts}
      updateSchoolAdminSchools={handleUpdateSchoolAdminSchools}
      impersonateSchoolAdmin={impersonateSchoolAdmin}
      schoolAdminsData={schoolAdminsData}
      newSchoolAdmin={newSchoolAdmin}
      isLoading={isLoading}
      errors={errors}
      removeSchoolAdmin={handleRemoveSchoolAdmin}
      schoolAdminNewAdmin={schoolAdminNewAdminHandler}
      defaultFiltered={defaultFiltered}
    />
  )
}
